import { Form, Formik, type FormikErrors, type FormikHelpers } from "formik";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import { SelectField } from "@/features/clients/components/record-form-fields";
import { listClientDocuments } from "@/features/documents/api";
import { createNotice, listNoticeDefinitions, updateNotice } from "@/features/notices/api";
import {
  type Notice,
  NOTICE_STATUS_OPTIONS,
  type NoticeDefinitionOption,
  type NoticePayload,
  type NoticeStatus,
} from "@/features/notices/types";
import { listPractitioners, type PractitionerOption } from "@/features/practitioners/api";
import { applyApiErrors } from "@/lib/form-errors";

interface NoticeFormValues {
  noticeDefinition: string;
  receivedOn: string;
  responseDeadline: string;
  status: NoticeStatus;
  taxYear: string;
  notes: string;
  assignedPractitioner: string;
  document: string;
}

const validationSchema = Yup.object({
  noticeDefinition: Yup.string().required("Select a notice type"),
  receivedOn: Yup.string().required("Received date is required"),
});

function toInitialValues(
  notice: Notice | null,
  defaultPractitioner: string,
  defaultNoticeDefinition: string,
): NoticeFormValues {
  return {
    noticeDefinition: notice?.noticeDefinition ?? defaultNoticeDefinition,
    receivedOn: notice?.receivedOn ?? new Date().toISOString().slice(0, 10),
    responseDeadline: notice?.responseDeadline ?? "",
    status: notice?.status ?? "OPEN",
    taxYear: notice?.taxYear ?? "",
    notes: notice?.notes ?? "",
    // Pre-select the logged-in user's default practitioner on a new notice.
    assignedPractitioner: notice?.assignedPractitioner ?? defaultPractitioner,
    document: notice?.document ?? "",
  };
}

function toPayload(v: NoticeFormValues): NoticePayload {
  return {
    notice_definition: v.noticeDefinition,
    received_on: v.receivedOn,
    response_deadline: v.responseDeadline || null,
    status: v.status,
    tax_year: v.taxYear.trim(),
    notes: v.notes,
    assigned_practitioner: v.assignedPractitioner || null,
    document: v.document || null,
  };
}

interface NoticeFormDialogProps {
  clientId: string;
  notice: Notice | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
  /** Pre-select a notice code on a new notice (e.g. from the advisor). */
  defaultNoticeDefinition?: string;
}

export function NoticeFormDialog({
  clientId,
  notice,
  open,
  onOpenChange,
  onSaved,
  defaultNoticeDefinition = "",
}: NoticeFormDialogProps) {
  const editing = notice !== null;
  const defaultPractitioner = useAuthStore((s) => s.user?.default_practitioner ?? "");
  const [definitions, setDefinitions] = useState<NoticeDefinitionOption[]>([]);
  const [practitioners, setPractitioners] = useState<PractitionerOption[]>([]);
  const [documents, setDocuments] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    if (!open) return;
    let active = true;
    listNoticeDefinitions()
      .then((list) => active && setDefinitions(list))
      .catch(() => active && setDefinitions([]));
    listPractitioners()
      .then((list) => active && setPractitioners(list))
      .catch(() => active && setPractitioners([]));
    listClientDocuments(clientId)
      .then(
        (docs) =>
          active && setDocuments(docs.map((d) => ({ value: d.id, label: d.name }))),
      )
      .catch(() => active && setDocuments([]));
    return () => {
      active = false;
    };
  }, [open, clientId]);

  const definitionOptions = definitions.map((d) => ({
    value: d.id,
    label: `${d.code} — ${d.title}`,
  }));
  const practitionerOptions = [
    { value: "", label: "Unassigned" },
    ...practitioners.map((p) => ({ value: p.id, label: p.name })),
  ];
  const documentOptions = [{ value: "", label: "No document" }, ...documents];

  async function handleSubmit(values: NoticeFormValues, bag: FormikHelpers<NoticeFormValues>) {
    try {
      if (editing) {
        await updateNotice(notice.id, toPayload(values));
      } else {
        await createNotice(clientId, toPayload(values));
      }
      toast.success(editing ? "Notice updated" : "Notice logged");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      const setErrors = bag.setErrors as (errors: FormikErrors<never>) => void;
      toast.error(applyApiErrors(err, { setErrors }, { fallback: "Couldn't save notice." }));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Notice" : "Log IRS Notice"}</DialogTitle>
        </DialogHeader>
        <Formik
          initialValues={toInitialValues(notice, defaultPractitioner, defaultNoticeDefinition)}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-4">
              <SelectField
                name="noticeDefinition"
                label="Notice Type"
                options={definitionOptions}
                placeholder="Select a notice code…"
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  name="receivedOn"
                  label="Received On"
                  labelClassName={FIELD_LABEL}
                  type="date"
                />
                <FormField
                  name="responseDeadline"
                  label="Response Deadline"
                  labelClassName={FIELD_LABEL}
                  type="date"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <SelectField name="status" label="Status" options={NOTICE_STATUS_OPTIONS} />
                <FormField
                  name="taxYear"
                  label="Tax Year"
                  labelClassName={FIELD_LABEL}
                  placeholder="e.g. 2022"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <SelectField
                  name="assignedPractitioner"
                  label="Assigned Practitioner"
                  options={practitionerOptions}
                  placeholder="Unassigned"
                />
                <SelectField
                  name="document"
                  label="Scanned Notice"
                  options={documentOptions}
                  placeholder="No document"
                />
              </div>
              <FormField
                name="notes"
                label="Notes"
                labelClassName={FIELD_LABEL}
                placeholder="Optional"
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {editing ? "Save Changes" : "Log Notice"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
